import { Box, Checkbox, Paper, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import React, { memo, useEffect, useState } from 'react';
import BaseTableHead from './BaseTableHead';
import styles from './BaseTable.module.scss';
import classNames from 'classnames/bind';
import { HeadCelType, SortDirection, SortField } from '~/utils/types/common';

type BaseTableProps<T extends { id: string }> = {
  data: Array<T>;
  renderCell: (item: T, index: number) => React.ReactElement;
  filterBy?: string;
  sortBy?: string;
  headCells: Array<HeadCelType>;
  page: number;
  totalPages: number;
  onChangePage?: (page: number) => void;
  onRequestSort?: (sortBy: Array<{ orderBy: string; order: SortDirection }>) => void;
  setSelectedIds: (selectedIds: Array<string>) => void;
  sort?: Array<SortField>;
  isCheckbox?: boolean;
  showPagination?: boolean;
};

const cx = classNames.bind(styles);

const BaseTable = <T extends { id: string }>(props: BaseTableProps<T>) => {
  //#region Destructuring Props
  const { data, renderCell, headCells, setSelectedIds, onRequestSort, onChangePage, totalPages, page } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [selected, setSelected] = useState<string[]>([]);
  const isSelected = (id: string) => selected.indexOf(id) !== -1;
  //#endregion Declare State

  //#region Implement Hook

  useEffect(() => {
    setSelectedIds(selected);
  }, [selected, setSelectedIds]);

  useEffect(() => {
    setSelected([]);
  }, [data]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleRequestSort = (property: string, sortDirection: SortDirection) => {
    if (onRequestSort) {
      onRequestSort([{ orderBy: property, order: sortDirection }]);
    }
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = data.map((item) => item.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event: React.ChangeEvent<unknown>, id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    if (onChangePage) {
      onChangePage(newPage);
    }
  };
  //#endregion Handle Function

  return (
    <Box className={cx('table-container')}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750, borderCollapse: "separate", borderSpacing: "0 4px", }}>
            <BaseTableHead
              numSelected={selected.length}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={data.length}
              headCells={headCells}
              sort={props.sort}
              isCheckbox={props.isCheckbox}
            />
            <TableBody>
              {data.map((item: T, index: number) => {
                return (
                  <TableRow
                    hover
                    role='checkbox'
                    tabIndex={-1}
                    key={item.id}
                    selected={isSelected(item.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    {props.isCheckbox &&
                      <TableCell padding='checkbox'>
                        <Checkbox
                          className='w-[20px] h-[20px]'
                          color='primary'
                          onChange={(event) => handleClick(event, item.id)}
                          checked={isSelected(item.id)}
                        />
                      </TableCell>
                    }
                    {renderCell(item, index)}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {props.showPagination !== false && totalPages > 0 &&
        <Box className={cx('table-pagination-container')}>
          
        </Box>
        }
      </Paper>
    </Box>
  );
};

export default memo(BaseTable) as typeof BaseTable;
